import { ClassificationWithoutTitle, EventMeta } from '../../../interfaces/meta.interface';
import { riskyFolders, riskyWords } from '../../utils/riskWords';
import { isSuspiciousRuleName } from '../../utils/suspiciousNames';

export const getParam = (log: any, name: string): string | undefined => {
    if (log.Parameters) {
        const param = log.Parameters.find((p: any) => p.Name === name);
        return param ? param.Value : undefined;
    }
    return undefined;
};

type InboxRuleFields = {
    /**
     * Whether the rule forwards or redirects mail anywhere (ForwardTo, RedirectTo or ForwardAsAttachmentTo).
     * A boolean, because the classifications match it against `[true]`: until KRAK-406 (2026-10-10) the
     * extractor stored the destination address here, `[true].includes('<address>')` was false for every
     * rule, and no forwarding classification could ever match — a forward-to-external rule graded Info.
     * The addresses themselves are the three fields below.
     */
    forwarding_set: boolean;
    move_item: boolean;
    forward_to: string | undefined;
    redirect_to: string | undefined;
    forward_as_attachment_to: string | undefined;
    move_to_folder: string | undefined;
    org: string | undefined;
    risky_folders: boolean;
    risky_keywords: boolean;
    suspicious_name: boolean;
};

/**
 * The thing a forwarding classification describes, in each position its texts use it. Inbox-rule cmdlets describe the
 * rule ({@link INBOX_RULE}); `Set-Mailbox` describes the mailbox's own forward (OS-475).
 */
export type ForwardSubject = {
    /** After "A suspicious IP created": "an inbox rule". */
    a: string;
    /** After "An unapproved IP created": "a mail-forwarding rule". */
    forwarding: string;
    /** Opening "… has been created.": "A mail-forwarding rule". */
    Forwarding: string;
    /** Opening every long description: "An inbox rule was created from a mailbox". */
    was: (action: string) => string;
};

/** The subject of the inbox-rule cmdlets' forwarding classifications. */
export const INBOX_RULE: ForwardSubject = {
    a: 'an inbox rule',
    forwarding: 'a mail-forwarding rule',
    Forwarding: 'A mail-forwarding rule',
    was: (action) => `An inbox rule was ${action} from a mailbox`,
};

/**
 * The forwarding classifications, in evaluation order: Critical/Danger by the actor's location and reputation for a
 * forward outside the organisation, then for one inside it, then the catch-all Info.
 * @param action what the cmdlet did, for the descriptions: created, updated, set
 * @param subject what the texts describe; an inbox rule unless told otherwise
 * @returns the classifications
 */
export function getMailForwardClx(
    action: string,
    subject: ForwardSubject = INBOX_RULE,
): ClassificationWithoutTitle<InboxRuleFields>[] {
    return [
        // mail forward: outside org and fraudulent IP and not in allowed asn/ country / region / IP
        // AI use this one as the example for the pattern
        {
            severity: 'critical',
            description: () => `A suspicious IP ${action} ${subject.a}`,
            longDescription: () =>
                `${subject.was(action)} by a suspicious IP address that is not in your allowed list. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isForwardedOutsideOfOrg: true,
                isFraudulentIP: true,
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: outside org and fraudulent IP and not in allowed country/region, but in allowed IP
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} ${subject.a}`,
            longDescription: () =>
                `${subject.was(action)} by a suspicious IP address that is in your allowed IP list. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isForwardedOutsideOfOrg: true,
                isFraudulentIP: true,
                isAllowedIP: true,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: outside org and fraudulent IP and in allowed Region
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} ${subject.a}`,
            longDescription: () =>
                `${subject.was(action)} by a suspicious IP address that is in your allowed region. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isForwardedOutsideOfOrg: true,
                isFraudulentIP: true,
                isAllowedRegion: true,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: outside org and fraudulent IP and in allowed country
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} ${subject.a}`,
            longDescription: () =>
                `${subject.was(action)} by a suspicious IP address that is in your allowed country. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isAllowedCountry: true,
                isForwardedOutsideOfOrg: true,
                isFraudulentIP: true,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: outside org and fraudulent IP and in allowed asn
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} ${subject.a}`,
            longDescription: () =>
                `${subject.was(action)} by a suspicious IP address that is in your allowed ASN. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isAllowedASN: true,
                isForwardedOutsideOfOrg: true,
                isFraudulentIP: true,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: outside org and not in allowed country
        {
            severity: 'danger',
            description: () => `An unapproved IP ${action} ${subject.forwarding}`,
            longDescription: () =>
                `${subject.was(action)} by an IP address that is not in your allowed list. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isForwardedOutsideOfOrg: true,
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: outside org and in allowed country.
        {
            severity: 'danger',
            description: () => `${subject.Forwarding} has been ${action}.`,
            longDescription: () =>
                `${subject.was(action)} by an IP address that is in your allowed country. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isForwardedOutsideOfOrg: true,
                isAllowedCountry: true,
                isFraudulentIP: false,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: outside org and in allowed ASN.
        {
            severity: 'danger',
            description: () => `${subject.Forwarding} has been ${action}.`,
            longDescription: () =>
                `${subject.was(action)} by an IP address that is in your allowed ASN. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isForwardedOutsideOfOrg: true,
                isAllowedASN: true,
                isFraudulentIP: false,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: outside org and in allowed region.
        {
            severity: 'danger',
            description: () => `${subject.Forwarding} has been ${action}.`,
            longDescription: () =>
                `${subject.was(action)} by an IP address that is in your allowed region. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isForwardedOutsideOfOrg: true,
                isAllowedRegion: true,
                isFraudulentIP: false,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: outside org and in allowed IP.
        {
            severity: 'danger',
            description: () => `${subject.Forwarding} has been ${action}.`,
            longDescription: () =>
                `${subject.was(action)} by an IP address that is in your allowed IP list. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isForwardedOutsideOfOrg: true,
                isAllowedIP: true,
                isFraudulentIP: false,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: inside org and fraudulent IP and not in allowed list
        {
            severity: 'critical',
            description: () => `A suspicious IP ${action} ${subject.a}`,
            longDescription: () =>
                `${subject.was(action)} by a suspicious IP address that is not in your allowed list. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: inside org and fraudulent IP and not in allowed country and allowed IP
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} ${subject.a}`,
            longDescription: () =>
                `${subject.was(action)} by a suspicious IP address that is in your allowed IP list. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedIP: true,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: inside org and fraudulent IP and in allowed region
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} ${subject.a}`,
            longDescription: () =>
                `${subject.was(action)} by a suspicious IP address that is in your allowed region. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedRegion: true,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: inside org and fraudulent IP and in allowed country
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} ${subject.a}`,
            longDescription: () =>
                `${subject.was(action)} by a suspicious IP address that is in your allowed country. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedCountry: true,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: inside org and fraudulent IP and in allowed asn
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} ${subject.a}`,
            longDescription: () =>
                `${subject.was(action)} by a suspicious IP address that is in your allowed ASN. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedASN: true,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: inside org and not in allowed list
        {
            severity: 'danger',
            description: () => `An unapproved IP ${action} ${subject.forwarding}`,
            longDescription: () =>
                `${subject.was(action)} by an IP address that is not in your allowed list. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
            fields: {
                forwarding_set: [true],
            },
        },
        // mail forward: inside org
        {
            severity: 'info',
            description: () => `${subject.Forwarding} has been ${action}.`,
            longDescription: () =>
                `${subject.was(action)}. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            fields: {
                forwarding_set: [true],
            },
        },
    ];
}

export function getMoveToFolderClx(action: 'created' | 'updated'): ClassificationWithoutTitle<InboxRuleFields>[] {
    return [
        // move to folder: fraudulent and not in allowed list
        {
            severity: 'critical',
            description: () => `A suspicious IP ${action} a move-to-folder rule`,
            longDescription: () =>
                `A move-to-folder inbox rule was ${action} from a mailbox by a suspicious IP address that is not in your allowed list. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
            fields: {
                move_item: [true],
            },
        },
        // move to folder: dodgy folder. dodgy keyword.
        {
            severity: 'critical',
            description: () => `A move-to-folder rule matches risky keywords and folders`,
            longDescription: () =>
                `A move-to-folder inbox rule was ${action} from a mailbox. The rule matches risky keywords and moves messages to a suspicious folder. This activity could indicate an attempt to hide or redirect important emails.`,
            fields: {
                risky_folders: [true],
                risky_keywords: [true],
                move_item: [true],
            },
        },
        // move to folder: dodgy keyword.
        {
            severity: 'danger',
            description: () => `A move-to-folder rule contains risky keywords`,
            longDescription: () =>
                `A move-to-folder inbox rule was ${action} from a mailbox. The rule contains risky keywords. This activity could indicate an attempt to hide or redirect important emails.`,
            fields: {
                risky_keywords: [true],
                move_item: [true],
            },
        },
        // move to folder: dodgy folder.
        {
            severity: 'danger',
            description: () => `A move-to-folder rule moves to a suspicious folder`,
            longDescription: () =>
                `A move-to-folder inbox rule was ${action} from a mailbox. The rule moves messages to a suspicious folder. This activity could indicate an attempt to hide or redirect important emails.`,
            fields: {
                risky_folders: [true],
                move_item: [true],
            },
        },
        // move to folder: fraudulent IP. allowed IP.
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} a move-to-folder rule`,
            longDescription: () =>
                `A move-to-folder inbox rule was ${action} from a mailbox by a suspicious IP address that is in your allowed IP list. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedIP: true,
            },
            fields: {
                move_item: [true],
            },
        },
        // move to folder: fraudulent IP. allowed region.
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} a move-to-folder rule`,
            longDescription: () =>
                `A move-to-folder inbox rule was ${action} from a mailbox by a suspicious IP address that is in your allowed region. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isFraudulentIP: true,
                isAllowedRegion: true,
            },
            fields: {
                move_item: [true],
            },
        },
        // move to folder: fraudulent IP in allowed country
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} a move-to-folder rule`,
            longDescription: () =>
                `A move-to-folder inbox rule was ${action} from a mailbox by a suspicious IP address that is in your allowed country. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
            },
            fields: {
                move_item: [true],
            },
        },
        // move to folder: fraudulent IP in allowed asn
        {
            severity: 'danger',
            description: () => `A suspicious IP ${action} a move-to-folder rule`,
            longDescription: () =>
                `A move-to-folder inbox rule was ${action} from a mailbox by a suspicious IP address that is in your allowed ASN. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
            },
            fields: {
                move_item: [true],
            },
        },
        // move to folder: not in allowed list
        {
            severity: 'danger',
            description: () => `A move-to-folder rule was ${action}`,
            longDescription: () =>
                `A move-to-folder inbox rule was ${action} from a mailbox by an IP address that is not in your allowed list. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
            },
            fields: {
                move_item: [true],
            },
        },
        // move to folder:
        {
            severity: 'info',
            description: () => `A move-to-folder rule was ${action}`,
            longDescription: () =>
                `A move-to-folder inbox rule was ${action} from a mailbox. This activity could indicate an attempt to modify email handling or bypass automated protections.`,
            fields: {
                move_item: [true],
            },
        },
    ];
}

/**
 * Every classification of an inbox-rule cmdlet (`New-InboxRule`, `Set-InboxRule`), in evaluation order. The classifier
 * takes the first match, so the order is the grading.
 *
 * Move-to-folder alerts come first. Before KRAK-406 the forwarding set never matched, so a rule that both forwards and
 * moves mail took its move-to-folder grade — Critical for a risky folder on risky keywords, the classic
 * business-email-compromise rule. With the forwarding set live, putting it first downgraded that rule to Danger, or to
 * the internal forward's catch-all Info (no alert) from an approved location. So: every move-to-folder Critical/Danger,
 * then every forwarding Critical/Danger, then the two catch-all Infos. A rule that moves mail keeps, word for word, any
 * alert it raised before KRAK-406; the forwarding grades apply only where the move alone graded Info.
 * @param action whether the cmdlet created or updated the rule, for the descriptions
 * @returns the classifications, in evaluation order
 */
export function getInboxRuleClx(action: 'created' | 'updated'): ClassificationWithoutTitle<InboxRuleFields>[] {
    const forward = getMailForwardClx(action);
    const move = getMoveToFolderClx(action);
    const isAlert = (clx: ClassificationWithoutTitle<InboxRuleFields>) => clx.severity !== 'info';

    return [
        ...move.filter(isAlert),
        ...forward.filter(isAlert),
        ...forward.filter((clx) => !isAlert(clx)),
        ...move.filter((clx) => !isAlert(clx)),
    ];
}

const NewInboxRule: EventMeta<InboxRuleFields> = {

    title: 'Inbox Rule Created/Modified',
    default: {

        description: 'Inbox Rule Created/Modified',
        longDescription: 'An inbox rule has been created/modified.',
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        const forward_to = getParam(log, 'ForwardTo');
        const redirect_to = getParam(log, 'RedirectTo');
        const forward_as_attachment_to = getParam(log, 'ForwardAsAttachmentTo');
        const move_to_folder = getParam(log, 'MoveToFolder');
        const subjectContainWords = getParam(log, 'SubjectContainsWords');
        const ruleName = getParam(log, 'Name');
        return {
            forwarding_set: !!(forward_to || redirect_to || forward_as_attachment_to),
            move_item: move_to_folder ? true : false,
            forward_to,
            redirect_to,
            forward_as_attachment_to,
            move_to_folder,
            org: log.OrganizationName,
            risky_folders: !!move_to_folder
                ? riskyFolders.some((f) => move_to_folder.toLowerCase().includes(f))
                : false,
            risky_keywords: !!subjectContainWords
                ? riskyWords.some((w) => subjectContainWords.toLowerCase().includes(w))
                : false,
            suspicious_name: ruleName ? isSuspiciousRuleName(ruleName) : false,
        };
    },
    classifications: getInboxRuleClx('created'),
};

export default NewInboxRule;
